/**
 * Start from a brief (PRD Amendment 5, A89–A95): the request and response of
 * `POST /api/studio/v1/feasibility/brief`. The route, the stub and the screen
 * share these; the real reader must return the same shape.
 */
import { z } from 'zod';

import { FeasibilityRequestSchema } from '../engine/request';

/** About 60,000 characters are read in one pass; longer is refused with the length (A89). */
export const MAX_BRIEF_CHARS = 60_000;
export const BRIEF_FILE_TYPES = [
    '.eml',
    '.pdf',
    '.docx',
    '.xlsx',
    '.csv',
    '.txt',
    // A screenshot of the email (sales feedback 2026-10-22).
    '.png',
    '.jpg',
    '.jpeg',
    '.webp',
] as const;
export const MAX_BRIEF_BYTES = 10 * 1024 * 1024;

/** Most files one brief takes (UAT UI-020: a client sent four location files). */
export const MAX_BRIEF_FILES = 5;
/** All the files together. */
export const MAX_BRIEF_TOTAL_BYTES = 20 * 1024 * 1024;

export const BriefFileSchema = z.object({
    name: z.string().min(1).max(260),
    size: z.number().int().min(0).max(MAX_BRIEF_BYTES),
    /** Text the browser could read itself (.eml, .txt). */
    text: z.string().max(MAX_BRIEF_CHARS).optional(),
    /** The file's bytes (PDF, Word, Excel, a screenshot), read on the server. */
    base64: z
        .string()
        .max(Math.ceil((MAX_BRIEF_BYTES * 4) / 3) + 4)
        .optional(),
});
export type BriefFile = z.infer<typeof BriefFileSchema>;

export const BriefInputSchema = z
    .object({
        text: z.string().max(MAX_BRIEF_CHARS).optional(),
        /** One attachment (the form before UAT UI-020); `files` takes several. */
        file: BriefFileSchema.optional(),
        files: z.array(BriefFileSchema).max(MAX_BRIEF_FILES).optional(),
    })
    .refine((b) => Boolean(b.text?.trim() || b.file || b.files?.length), {
        message: 'Paste the brief or attach a file.',
    })
    .refine(
        (b) =>
            [...(b.file ? [b.file] : []), ...(b.files ?? [])].reduce((n, f) => n + f.size, 0) <=
            MAX_BRIEF_TOTAL_BYTES,
        { message: 'The files are over 20 MB together. Attach the brief and the lists only.' },
    );
export type BriefInput = z.infer<typeof BriefInputSchema>;

/** Where a field's value came from (A91). */
export const FieldSourceSchema = z.object({
    kind: z.enum(['brief', 'suggested']),
    /** The sentence of the email, or the questionnaire question it was read from. */
    passage: z.string(),
});
export type FieldSource = z.infer<typeof FieldSourceSchema>;

export const BriefDraftSchema = z.object({
    key: z.string(),
    title: z.string(),
    request: FeasibilityRequestSchema,
    /** Keyed by request field (`target`, `incidenceRate`, `fieldDays`, `age`, `states`, `quotas` …). */
    sources: z.record(z.string(), FieldSourceSchema),
    /** What the brief does not say, asked back, never guessed (A93). */
    questions: z.array(z.string()),
    /** Things it asks for that the tool cannot target yet. */
    cannotTarget: z.array(z.string()),
    /**
     * Nobody stated the share who qualify and it could not be predicted, so the
     * request carries a default the person must replace or confirm before it runs.
     */
    incidenceUnconfirmed: z.boolean().optional(),
    /** Where a suggested share who qualify came from, shown while it waits to be confirmed. */
    incidenceNote: z.string().optional(),
    /** A draft from DANI's plan: the signed reading `POST /checks` records (mode `strategy`). */
    daniReceipt: z.string().optional(),
});
export type BriefDraft = z.infer<typeof BriefDraftSchema>;

export const BriefResponseSchema = z.object({
    briefId: z.string(),
    extractor: z.enum(['text', 'mime', 'mammoth', 'unpdf', 'xlsx', 'mfour-agents', 'image']),
    fileName: z.string().nullable(),
    charCount: z.number().int(),
    /** One per study the brief asks for (A92). */
    drafts: z.array(BriefDraftSchema),
    /** Sentences addressed to the tool, which were ignored (A94). */
    ignoredInstructions: z.array(z.string()),
    /**
     * Location lists read from the attachment, which never count toward the
     * brief's length: what was read, in one line ("995 test and 3,000 control locations").
     */
    locations: z.string().optional(),
    /** A panelist ID list read from the attachment, in one line ("2,607 panelist IDs from list.csv"). */
    panelists: z.string().optional(),
});
export type BriefResponse = z.infer<typeof BriefResponseSchema>;

/** A brief read on the Files page waits here (session storage) for the check page to open it. */
export const BRIEF_STORAGE_PREFIX = 'feasibility-brief:';
